import { useSearchParams } from "react-router";
import type { WorkflowGraphDensity, WorkflowGraphLayout } from "../../../api/userPreferencesApi";
import { useUserPreferences } from "../../profile/preferences/userPreferences";
import { PROCESS_DENSITY_PARAMETER, PROCESS_DISPLAY_PARAMETER, PROCESS_LAYOUT_PARAMETER, processDensityOf, processDisplayOf, processLayoutOf, type ProcessDisplay } from "./processDisplay";

export interface ProcessViewChoices {
  readonly display: ProcessDisplay;
  readonly setDisplay: (display: ProcessDisplay) => void;
  readonly density: WorkflowGraphDensity;
  readonly setDensity: (density: WorkflowGraphDensity) => void;
  readonly layout: WorkflowGraphLayout;
  readonly setLayout: (layout: WorkflowGraphLayout) => void;
}

/**
 * How a graph page shows its Workflow, the Workflow graph's and the run graph's alike: the
 * display, the density and the direction are the view's own, in its address, and the
 * preferences are only the defaults (F76, F59).
 */
export function useProcessViewChoices(): ProcessViewChoices {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaults = useUserPreferences().preferences;
  const set = (parameter: string, value: string | null) => {
    setSearchParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (value === null) {
          updated.delete(parameter);
        } else {
          updated.set(parameter, value);
        }
        return updated;
      },
      { replace: true },
    );
  };
  return {
    display: processDisplayOf(searchParams.get(PROCESS_DISPLAY_PARAMETER)),
    setDisplay: (display) => { set(PROCESS_DISPLAY_PARAMETER, display === "graph" ? null : display); },
    density: processDensityOf(searchParams.get(PROCESS_DENSITY_PARAMETER), defaults.workflowGraphDensity),
    setDensity: (density) => { set(PROCESS_DENSITY_PARAMETER, density); },
    layout: processLayoutOf(searchParams.get(PROCESS_LAYOUT_PARAMETER), defaults.workflowGraphLayout),
    setLayout: (layout) => { set(PROCESS_LAYOUT_PARAMETER, layout); },
  };
}
